import { getPurchaseAvailability } from './api';

const BLOCKED_BROWSE_STATES = new Set(['temporarily_locked', 'payment_pending', 'sold']);

/** Whether the PageHop availability overlay permits a new purchase. */
export const isListingPurchasable = listing =>
  listing?.attributes?.purchaseAvailability
    ? listing.attributes.purchaseAvailability.purchasable === true
    : true;

const isListingBlockedForBrowse = listing =>
  BLOCKED_BROWSE_STATES.has(listing?.attributes?.purchaseAvailability?.state);

/** Apply public purchase availability to listing API reads, leaving history and own listings intact. */
export const withPurchaseAvailability = sdk => {
  if (!sdk?.listings || sdk.pageHopPurchaseAvailability) return sdk;
  const wrapped = Object.create(sdk);
  wrapped.pageHopPurchaseAvailability = true;
  wrapped.listings = Object.create(sdk.listings);
  const tracked = listing => {
    const data = listing.attributes?.publicData || {};
    return (
      (!data.listingType || ['book-listing', 'collector-merch'].includes(data.listingType)) &&
      (!data.transactionProcessAlias ||
        data.transactionProcessAlias.startsWith('default-purchase/'))
    );
  };
  const annotate = async (response, filter) => {
    const data = response?.data?.data;
    const listings = Array.isArray(data) ? data : data ? [data] : [];
    const ids = listings
      .filter(tracked)
      .map(listing => listing.id?.uuid)
      .filter(Boolean);
    if (!ids.length) return response;
    let availability;
    try {
      const result = sdk.listingAvailability
        ? await sdk.listingAvailability(ids)
        : await getPurchaseAvailability(ids);
      availability = result.availability;
    } catch (error) {
      availability = {};
    }
    const annotated = listings.map(listing =>
      !tracked(listing)
        ? listing
        : {
            ...listing,
            attributes: {
              ...listing.attributes,
              purchaseAvailability: availability?.[listing.id?.uuid] || {
                state: 'unverified',
                purchasable: false,
              },
            },
          }
    );
    const filteredListings = filter
      ? annotated.filter(listing => !isListingBlockedForBrowse(listing))
      : annotated;
    const meta = response.data.meta;
    const hasCompleteSinglePage =
      filter &&
      Array.isArray(data) &&
      meta?.totalPages === 1 &&
      meta.totalItems === data.length;
    return {
      ...response,
      data: {
        ...response.data,
        ...(hasCompleteSinglePage
          ? {
              meta: {
                ...meta,
                totalItems: filteredListings.length,
                totalPages: filteredListings.length ? 1 : 0,
              },
            }
          : {}),
        data: Array.isArray(data) ? filteredListings : annotated[0],
      },
    };
  };
  if (sdk.listings.query)
    wrapped.listings.query = (...args) =>
      sdk.listings.query(...args).then(response => annotate(response, true));
  if (sdk.listings.show)
    wrapped.listings.show = (...args) =>
      sdk.listings.show(...args).then(response => annotate(response, false));
  return wrapped;
};
